import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { hexToRgba } from '../services/hex.service';

type TextTransformType = 'lowercase' | 'uppercase' | 'none';

export interface BadgeProps extends React.AllHTMLAttributes<HTMLDivElement> {
  appearance: 'default' | 'primary' | 'warning' | 'success' | 'danger';
  text: string | number;
  rounded?: boolean;
  justifyContent?: 'center' | 'left' | 'end';
  icon?: any;
  color?: string;
  textColor?: string;
  withTextTranslator?: boolean;
  textTransform?: TextTransformType;
  title?: string;
}

export const StyledBadgeText = styled.div<{
  textTransform: TextTransformType;
}>`
  font-size: 10px;
  line-height: 10px;
  text-transform: ${props => props.textTransform};
`;
const StyledBadgeRounded = css`
  border-radius: 8px;
`;
export const StyledBadgeWarning = css`
  background: ${({ theme }) => hexToRgba(theme.colors.ORANGE.O400, 0.4)};

  ${StyledBadgeText} {
    color: ${({ theme }) => theme.colors.ORANGE.O400};
  }
`;
export const StyledBadgeDanger = css`
  background: ${({ theme }) => hexToRgba(theme.colors.RED.R400, 0.4)};

  ${StyledBadgeText} {
    color: ${({ theme }) => theme.colors.RED.R400};
  }
`;
export const StyledBadgeSuccess = css`
  background: ${({ theme }) => hexToRgba(theme.colors.GREEN.G400, 0.4)};

  ${StyledBadgeText} {
    color: ${({ theme }) => theme.colors.GREEN.G400};
  }
`;
export const StyledBadgePrimary = css`
  background: ${({ theme }) => hexToRgba(theme.colors.BLUE.B400, 0.4)};

  ${StyledBadgeText} {
    color: ${({ theme }) => theme.colors.BLUE.B400};
  }
`;
export const StyledBadgeCustom = css<{
  color?: string;
  textColor?: string;
}>`
  background: ${({ color, theme }) =>
    color?.match('^#(?:[0-9a-fA-F]{3}){1,2}$')
      ? color
      : hexToRgba(theme.colors[color || 'LABEL'], 0.4)};
  color: ${({ textColor, theme }) =>
    textColor
      && (theme.colors[textColor] || textColor)
      }!important;
  & > * {
    color: ${({textColor}) => textColor && 'inherit!important'};
  }
`;
export const StyledBadgeJustifyLeft = css`
  justify-content: left;
`;
export const StyledBadgeJustifyEnd = css`
  justify-content: end;
`;
export const StyledBadge = styled.div<{
  appearance: string;
  rounded: boolean;
  justifyContent?: string;
  color?: string;
  textColor?: string;
}>`
  text-align: center;
  padding: 3px 7px;
  background: ${({ theme }) => hexToRgba(theme.colors.LABEL, 0.4)};
  border-radius: 4px;
  transition: all 0.3s;

  ${StyledBadgeText} {
    color: ${({ theme }) => theme.colors.LABEL};
  }

  ${props => props.appearance === 'primary' && StyledBadgePrimary};
  ${props => props.appearance === 'success' && StyledBadgeSuccess};
  ${props => props.appearance === 'danger' && StyledBadgeDanger};
  ${props => props.appearance === 'warning' && StyledBadgeWarning};
  ${props => props.rounded && StyledBadgeRounded};
  ${props => props.justifyContent === 'left' && StyledBadgeJustifyLeft};
  ${props => props.justifyContent === 'end' && StyledBadgeJustifyEnd};
  ${props => props.color && StyledBadgeCustom};
`;

const BadgeComponent = ({
  appearance = 'default',
  text,
  rounded = false,
  justifyContent = 'center',
  className,
  color,
  textColor,
  children,
  withTextTranslator = true,
  textTransform = 'uppercase',
  title,
  style,
}: BadgeProps) => {
  const { t } = useTranslation();
  return (
    <StyledBadge
      appearance={appearance}
      rounded={rounded}
      justifyContent={justifyContent}
      className={className}
      color={color}
      textColor={textColor}
      title={title}
      style={style}
    >
      <StyledBadgeText textTransform={textTransform}>
        {typeof text === 'string' && withTextTranslator ? t(`${text}`) : text}
      </StyledBadgeText>
      {children}
    </StyledBadge>
  );
};

export default BadgeComponent;